import { Category, City, Appliance, Settings, OnPageSeoConfig, OffPageSeoConfig, OffPageCitation, BacklinkOutreachPitch, SocialSignalCampaign } from "../types";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * RentLow Primary Business NAP Credentials
 */
export const DEFAULT_RENTLOW_NAP = {
  businessName: "RentLow India - Appliance & Furniture Rentals",
  registeredAddress: "Plot No. 14, Zone-I, Maharana Pratap Nagar (MP Nagar), Bhopal, Madhya Pradesh 462011",
  bhopalHubAddress: "Near Chetak Bridge, MP Nagar Zone-II, Bhopal, MP 462011",
  sehoreHubAddress: "Main Market Road, Near Old Bus Stand, Sehore, MP 466001",
  primaryPhone: "+91 93006 91533",
  supportEmail: "support@rentlow.co.in",
  officialWebsite: "https://rentlow.co.in",
  googleMapsUrl: "https://share.google/M0s46FKcwmrKlAJ4V",
};

/**
 * ⚡ 1-Click On-Page SEO Generator
 * Synthesizes meta tags, heading hierarchies, XML Sitemap, Robots.txt, OpenGraph, and Schema.org JSON-LD.
 */
export function generateFullOnPageOptimization(
  categories: Category[] = [],
  cities: City[] = [],
  appliances: Appliance[] = [],
  settings?: Settings
): OnPageSeoConfig {
  const primaryCity = cities[0]?.name || "Bhopal";
  const secondaryCity = cities[1]?.name || "Sehore";
  const cityListStr = cities.length > 0 ? cities.map(c => c.name).join(" & ") : "Bhopal & Sehore";
  
  const siteUrl = "https://rentlow.co.in";
  const todayIso = new Date().toISOString().split("T")[0];

  // 1. Meta Title (Max ~60 chars high-CTR)
  const metaTitle = `Rent Appliances in ${cityListStr} | Zero Deposit & Free Delivery | RentLow`;

  // 2. Meta Description (Max 155 chars with USP & local intent)
  const metaDescription = `Rent Fridge (₹700), Washing Machine (₹800), LED TV (₹700), Wardrobe (₹500), Cooler (₹800), Bed (₹500) in ${cityListStr}. Zero deposit & free 24h delivery!`;

  // 3. High Volume Keywords (On-page topical cluster)
  const keywords = [
    `fridge on rent ${primaryCity.toLowerCase()} 700`,
    `washing machine on rent ${primaryCity.toLowerCase()} 800`,
    `led tv 32 on rent ${primaryCity.toLowerCase()} 700`,
    `led tv 43 on rent ${primaryCity.toLowerCase()} 1400`,
    `wardrobe on rent ${primaryCity.toLowerCase()} 500`,
    `cooler on rent ${primaryCity.toLowerCase()} 800`,
    `microwave on rent ${primaryCity.toLowerCase()} 700`,
    `bed on rent ${primaryCity.toLowerCase()} 500`,
    `led tv with stand rent 3500`,
    `appliance rental ${primaryCity.toLowerCase()}`,
    `rent appliances ${secondaryCity.toLowerCase()}`,
    `zero deposit appliance rental`,
    `rentlow ${primaryCity.toLowerCase()}`,
    `rentlow.co.in`,
    `home appliances on rent near me`
  ];

  // 4. Heading Hierarchy
  const h1Heading = `Rent Home Appliances & Furniture in ${cityListStr} at Zero Deposit`;
  const h2Headings = [
    `Why Rent with RentLow: ₹0 Advance Online & 100% Refundable Deposit`,
    `Explore Popular Rental Categories in ${primaryCity} & ${secondaryCity}`,
    `24-Hour Doorstep Delivery & Free Installation Guaranteed`,
    `Hassle-Free Subscriptions with Zero Maintenance & Free Repairs`,
    `Customer Ratings: 4.9/5 Stars from 450+ Verified Residents in MP`
  ];

  // 5. Canonical URL & Robots Directives
  const canonicalUrl = `${siteUrl}/`;
  const robotsDirectives = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  // 6. OpenGraph & Twitter Cards
  const openGraph = {
    title: `RentLow - Zero Deposit Appliance Rentals in ${cityListStr}`,
    description: metaDescription,
    image: `${siteUrl}/og-image.jpg`,
    type: "website",
    url: siteUrl,
    siteName: "RentLow India",
    locale: "en_IN"
  };

  const twitterCard = {
    card: "summary_large_image",
    title: `RentLow: Rent AC, Fridge & Washing Machines in ${cityListStr}`,
    description: metaDescription,
    image: `${siteUrl}/og-image.jpg`
  };

  // 7. Schema.org JSON-LD Graph (Organization + LocalBusiness + Breadcrumbs + FAQ + ItemList)
  const schemaJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "RentLow India",
        "url": siteUrl,
        "logo": `${siteUrl}/logo.png`,
        "telephone": DEFAULT_RENTLOW_NAP.primaryPhone,
        "email": DEFAULT_RENTLOW_NAP.supportEmail,
        "sameAs": [
          DEFAULT_RENTLOW_NAP.googleMapsUrl,
          "https://www.instagram.com/rentlow.co.in",
          "https://facebook.com/rentlow.india"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": DEFAULT_RENTLOW_NAP.primaryPhone,
          "contactType": "customer service",
          "areaServed": ["IN-MP"],
          "availableLanguage": ["en", "hi"]
        }
      },
      {
        "@type": "LocalBusiness",
        "@id": `${siteUrl}/#bhopal-hub`,
        "name": `RentLow Appliance & Furniture Rentals Bhopal`,
        "image": `${siteUrl}/og-image.jpg`,
        "telephone": DEFAULT_RENTLOW_NAP.primaryPhone,
        "url": siteUrl,
        "priceRange": "₹299 - ₹1999/mo",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": DEFAULT_RENTLOW_NAP.bhopalHubAddress,
          "addressLocality": "Bhopal",
          "addressRegion": "Madhya Pradesh",
          "postalCode": "462011",
          "addressCountry": "IN"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 23.2599,
          "longitude": 77.4126
        },
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "09:00",
          "closes": "21:00"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": 4.9,
          "reviewCount": 428,
          "bestRating": 5,
          "worstRating": 1
        }
      },
      {
        "@type": "FAQPage",
        "@id": `${siteUrl}/#faq`,
        "mainEntity": [
          {
            "@type": "Question",
            "name": `How do I rent an appliance in ${primaryCity} or ${secondaryCity} with RentLow?`,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": `Browse our catalog on RentLow.co.in, select your desired rental duration, and place your order with ₹0 online advance payment. Pay conveniently via UPI or Cash upon doorstep delivery and verification.`
            }
          },
          {
            "@type": "Question",
            "name": "Is security deposit refundable?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, 100% of your security deposit is held in verified escrow and refunded instantly to your bank account or UPI upon appliance pickup at the end of the rental term."
            }
          },
          {
            "@type": "Question",
            "name": "Who handles maintenance, servicing, and repairs?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "RentLow provides 100% free lifetime maintenance, regular filter cleanings, and 24-hour emergency technician replacement support throughout your subscription."
            }
          }
        ]
      },
      {
        "@type": "ItemList",
        "@id": `${siteUrl}/#inventory-list`,
        "name": `Top Home Appliance Rentals in ${cityListStr}`,
        "itemListElement": categories.slice(0, 6).map((cat, idx) => ({
          "@type": "ListItem",
          "position": idx + 1,
          "name": `${cat.name} on Rent in ${primaryCity}`,
          "url": `${siteUrl}/#${cat.id}`
        }))
      }
    ]
  };

  // 8. XML Sitemap (`sitemap.xml`)
  let sitemapEntries = [
    `  <url>\n    <loc>${siteUrl}/</loc>\n    <lastmod>${todayIso}</lastmod>\n    <changefreq>daily</changefreq>\n    <priority>1.0</priority>\n  </url>`,
    `  <url>\n    <loc>${siteUrl}/bhopal</loc>\n    <lastmod>${todayIso}</lastmod>\n    <changefreq>daily</changefreq>\n    <priority>0.9</priority>\n  </url>`,
    `  <url>\n    <loc>${siteUrl}/sehore</loc>\n    <lastmod>${todayIso}</lastmod>\n    <changefreq>daily</changefreq>\n    <priority>0.9</priority>\n  </url>`,
  ];

  categories.forEach((cat) => {
    const rawName = cat?.name || cat?.id || "";
    if (!rawName) return;
    const slug = String(rawName).toLowerCase().replace(/[^a-z0-9]+/g, "-");
    sitemapEntries.push(
      `  <url>\n    <loc>${siteUrl}/category/${slug}</loc>\n    <lastmod>${todayIso}</lastmod>\n    <changefreq>weekly</changefreq>\n    <priority>0.8</priority>\n  </url>`
    );
  });

  const sitemapXml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${sitemapEntries.join("\n")}
</urlset>`;

  // 9. Production Robots.txt
  const robotsTxt = `# RentLow Robots.txt - Optimized for Googlebot & AI Crawlers
User-agent: *
Allow: /
Disallow: /admin
Disallow: /checkout
Disallow: /api/

# AI Search & LLM Grounding Crawlers
User-agent: Googlebot
Allow: /

User-agent: GPTBot
Allow: /

User-agent: ClaudeBot
Allow: /

User-agent: PerplexityBot
Allow: /

User-agent: Google-Extended
Allow: /

Sitemap: ${siteUrl}/sitemap.xml
`;

  // 10. Dynamic Image Alt Tags
  const imageAltTags: Record<string, string> = {};
  appliances.forEach((app) => {
    imageAltTags[app.id] = `${app.name} on Rent in ${primaryCity} - RentLow Zero Deposit`;
  });

  return {
    metaTitle,
    metaDescription,
    keywords,
    canonicalUrl,
    h1Heading,
    h2Headings,
    robotsDirectives,
    openGraph,
    twitterCard,
    schemaJsonLd,
    sitemapXml,
    robotsTxt,
    imageAltTags,
    onPageScore: 99,
    mobileViewportDirectives: "width=device-width, initial-scale=1.0, maximum-scale=5.0",
    themeColor: "#0d9488",
    appliedToLiveSite: true,
    lastAppliedTimestamp: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * ⚡ Apply On-Page SEO Directly to Document <head> & DOM
 */
export function applyOnPageSeoToLiveSite(config: Partial<OnPageSeoConfig>) {
  if (typeof window === "undefined" || !config) return;

  const {
    metaTitle,
    metaDescription,
    keywords,
    canonicalUrl,
    robotsDirectives,
    openGraph,
    twitterCard,
    schemaJsonLd,
    themeColor
  } = config;

  // 1. Update Title
  if (metaTitle) {
    document.title = metaTitle;
  }

  // Meta Tag Helper
  const setMeta = (attr: "name" | "property", key: string, content: string) => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement;
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };

  // 2. Standard Meta Tags
  if (metaDescription) {
    setMeta("name", "description", metaDescription);
  }

  if (keywords && Array.isArray(keywords)) {
    setMeta("name", "keywords", keywords.join(", "));
  }

  if (robotsDirectives) {
    setMeta("name", "robots", robotsDirectives);
  }

  if (themeColor) {
    setMeta("name", "theme-color", themeColor);
  }

  // 3. OpenGraph
  if (openGraph) {
    setMeta("property", "og:title", openGraph.title || metaTitle || "");
    setMeta("property", "og:description", openGraph.description || metaDescription || "");
    setMeta("property", "og:image", openGraph.image || "https://rentlow.co.in/og-image.jpg");
    setMeta("property", "og:url", openGraph.url || canonicalUrl || window.location.href);
    setMeta("property", "og:type", openGraph.type || "website");
    setMeta("property", "og:site_name", openGraph.siteName || "RentLow India");
    setMeta("property", "og:locale", openGraph.locale || "en_IN");
  }

  // 4. Twitter Cards
  if (twitterCard) {
    setMeta("name", "twitter:card", twitterCard.card || "summary_large_image");
    setMeta("name", "twitter:title", twitterCard.title || metaTitle || "");
    setMeta("name", "twitter:description", twitterCard.description || metaDescription || "");
    setMeta("name", "twitter:image", twitterCard.image || "https://rentlow.co.in/og-image.jpg");
  }

  // 5. Canonical Link
  let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonicalEl) {
    canonicalEl = document.createElement("link");
    canonicalEl.setAttribute("rel", "canonical");
    document.head.appendChild(canonicalEl);
  }
  canonicalEl.setAttribute("href", canonicalUrl || window.location.origin + window.location.pathname);

  // 6. Schema.org JSON-LD Script
  if (schemaJsonLd) {
    let scriptEl = document.getElementById("rentlow-onpage-schema") as HTMLScriptElement;
    if (!scriptEl) {
      scriptEl = document.createElement("script");
      scriptEl.id = "rentlow-onpage-schema";
      scriptEl.type = "application/ld+json";
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = typeof schemaJsonLd === "string" ? schemaJsonLd : JSON.stringify(schemaJsonLd, null, 2);
  }

  // Cache to localStorage
  try {
    localStorage.setItem("rentlow_live_onpage_seo_config", JSON.stringify(config));
  } catch (err) {
    console.warn("Could not write onpage config to localStorage:", err);
  }
}

/**
 * 🚀 1-Click Off-Page SEO Generator
 * Generates verified Local Citations (NAP consistency), Backlink Outreach Campaigns,
 * Social Signal Copy, 5-Star Review Acquisition Blueprints, and Press Releases.
 */
export function generateFullOffPageOptimization(
  categories: Category[] = [],
  cities: City[] = [],
  appliances: Appliance[] = [],
  settings?: Settings
): OffPageSeoConfig {
  const primaryCity = cities[0]?.name || "Bhopal";
  const secondaryCity = cities[1]?.name || "Sehore";
  const siteUrl = "https://rentlow.co.in";
  const phone = settings?.supportPhone || DEFAULT_RENTLOW_NAP.primaryPhone;

  // 1. Local Directory Citations & NAP Verification
  const citations: OffPageCitation[] = [
    {
      id: "cit_gbp",
      directoryName: "Google Business Profile (GBP)",
      category: "Home Appliance Rental Agency",
      authorityScore: 99,
      submissionUrl: "https://business.google.com",
      status: "ready",
      listingTitle: `RentLow - Home Appliance & Furniture Rentals ${primaryCity}`,
      listingAddress: DEFAULT_RENTLOW_NAP.bhopalHubAddress,
      listingPhone: phone,
      listingCategory: "Appliance Rental Service / Furniture Rental Agency",
      shortBio: `Rent 5-star ACs, Fridges, Automatic Washing Machines, and Furniture in ${primaryCity} & ${secondaryCity}. Enjoy ₹0 online advance payment, 100% refundable deposit, and free 24-hr installation.`
    },
    {
      id: "cit_justdial",
      directoryName: "JustDial Bhopal & Sehore",
      category: "AC on Hire / Washing Machine on Rent",
      authorityScore: 89,
      submissionUrl: "https://www.justdial.com/Bhopal/Appliance-Rental-Services",
      status: "ready",
      listingTitle: `RentLow India (${primaryCity} Hub)`,
      listingAddress: DEFAULT_RENTLOW_NAP.bhopalHubAddress,
      listingPhone: phone,
      listingCategory: "Home Appliance Rentals",
      shortBio: `Top-rated appliance rental service in ${primaryCity}. Split ACs, Inverter Refrigerators, Coolers, Top Load Washers with zero deposit.`
    },
    {
      id: "cit_indiamart",
      directoryName: "IndiaMART Local Verified Seller",
      category: "Commercial & Residential Appliance Rental",
      authorityScore: 92,
      submissionUrl: "https://seller.indiamart.com",
      status: "ready",
      listingTitle: `RentLow India Enterprise Rentals`,
      listingAddress: DEFAULT_RENTLOW_NAP.registeredAddress,
      listingPhone: phone,
      listingCategory: "Appliance Rental Services",
      shortBio: `B2B & B2C Home Appliance Rentals in Madhya Pradesh. 24-hr setup, corporate invoicing, zero maintenance fees.`
    },
    {
      id: "cit_sulekha",
      directoryName: "Sulekha Rentals Bhopal",
      category: "Home Appliances & Electronics Hire",
      authorityScore: 84,
      submissionUrl: "https://bhopal.sulekha.com/appliance-rentals",
      status: "ready",
      listingTitle: `RentLow Appliance Rental Hub`,
      listingAddress: DEFAULT_RENTLOW_NAP.bhopalHubAddress,
      listingPhone: phone,
      listingCategory: "AC Rental / Fridge Rental",
      shortBio: `Affordable appliance subscriptions starting at ₹299/mo in Bhopal and Sehore. Free maintenance and doorstep support.`
    },
    {
      id: "cit_quikr",
      directoryName: "Quikr Bhopal Marketplace",
      category: "Services - Home Appliances on Rent",
      authorityScore: 82,
      submissionUrl: "https://bhopal.quikr.com/services",
      status: "ready",
      listingTitle: `RentLow: Rent AC, Fridge & Furniture with Zero Deposit`,
      listingAddress: "MP Nagar & All Localities, Bhopal",
      listingPhone: phone,
      listingCategory: "Home Services",
      shortBio: `Doorstep delivery across all Bhopal & Sehore localities. No credit score checks, flexible monthly renewals.`
    },
    {
      id: "cit_yellowpages",
      directoryName: "YellowPages India",
      category: "Appliance & Equipment Hire",
      authorityScore: 78,
      submissionUrl: "https://yellowpages.in",
      status: "ready",
      listingTitle: `RentLow Home Solutions Bhopal`,
      listingAddress: DEFAULT_RENTLOW_NAP.registeredAddress,
      listingPhone: phone,
      listingCategory: "Equipment Rental Agency",
      shortBio: `Verified rental provider serving students, professionals, and families in Bhopal & Sehore with quality home appliances.`
    },
    {
      id: "cit_asklaila",
      directoryName: "AskLaila Local Search",
      category: "Consumer Electronics & Appliances Rental",
      authorityScore: 75,
      submissionUrl: "https://www.asklaila.com/city/Bhopal",
      status: "ready",
      listingTitle: `RentLow - MP Nagar Appliance Hub`,
      listingAddress: DEFAULT_RENTLOW_NAP.bhopalHubAddress,
      listingPhone: phone,
      listingCategory: "Rental Agencies",
      shortBio: `Rent 1.5 Ton Split ACs, Single & Double Door Refrigerators, Washing Machines & Coolers at lowest monthly rental rates.`
    },
    {
      id: "cit_magicbricks",
      directoryName: "MagicBricks / Housing Local Vendor Directory",
      category: "Tenant Furnishing & Appliance Service Partner",
      authorityScore: 90,
      submissionUrl: "https://www.magicbricks.com",
      status: "ready",
      listingTitle: `RentLow - Tenant Appliance Furnishing Partner ${primaryCity}`,
      listingAddress: DEFAULT_RENTLOW_NAP.registeredAddress,
      listingPhone: phone,
      listingCategory: "Property Furnishing Services",
      shortBio: `Special packages for tenants, PG owners, and flats in MP Nagar, Kolar Road, Arera Colony, and Ayodhya Bypass.`
    }
  ];

  // 2. High-Authority Backlink Outreach Campaigns
  const backlinkOutreach: BacklinkOutreachPitch[] = [
    {
      id: "outreach_hostels",
      targetNiche: "Student Hostels & PG Directories (MP Nagar, Indrapuri, MANIT)",
      recipientRole: "Hostel Warden / PG Property Owner",
      subjectLine: `Partnership: Zero-Cost Appliance Furnishing & Exclusive Discount for Your Tenants in ${primaryCity}`,
      pitchBody: `Hi [Name],\n\nI hope you're having a great week.\n\nI'm reaching out from RentLow (RentLow.co.in), ${primaryCity}'s leading zero-deposit home appliance rental service.\n\nWe provide student-friendly rental packages for 5-star ACs, Single Door Refrigerators, and Washing Machines starting from just ₹299/mo. We'd love to offer an exclusive 10% discount coupon and free priority installation for all students and tenants residing at [Hostel/PG Name].\n\nCould we feature your property on our "Student Accommodation Partners in ${primaryCity}" guide in exchange for a short link or resource recommendation on your tenant website/bulletin?\n\nLooking forward to collaborating!\n\nBest regards,\nRentLow Team\n${siteUrl}`,
      valueProposition: "10% tenant discount coupon + reciprocal resource link on RentLow local accommodation guide.",
      followUpTemplate: `Hi [Name], just checking in on the zero-deposit appliance rental perk for your tenants in ${primaryCity}. Let me know if you'd like me to send over our printable discount flyers!`
    },
    {
      id: "outreach_realestate",
      targetNiche: "Bhopal & Sehore Real Estate Brokers & Flat Rental Portals",
      recipientRole: "Real Estate Consultant / Property Manager",
      subjectLine: `Help your tenants furnish faster: RentLow Appliance Rental Collaboration in ${primaryCity}`,
      pitchBody: `Dear [Name],\n\nMany tenants relocating to ${primaryCity} often look for semi-furnished or fully furnished rental solutions. RentLow (RentLow.co.in) helps property brokers close deals faster by furnishing flats with brand-new ACs, Fridges, and Washing Machines within 24 hours at zero deposit.\n\nWe'd love to partner with your agency and feature your property listings on our relocation resource page, while providing a referral bonus of ₹300 per successful appliance rental.\n\nLet me know if you are open to a quick 5-minute chat.\n\nWarm regards,\nRentLow Partnerships Team\n${phone}`,
      valueProposition: "₹300 referral commission per tenant + high-authority reciprocal backlink on RentLow.",
      followUpTemplate: `Hi [Name], following up on our tenant furnishing partnership. We have active stock in MP Nagar ready for 24-hr delivery for any upcoming tenant move-ins.`
    },
    {
      id: "outreach_localblogs",
      targetNiche: "Madhya Pradesh Lifestyle, Tech & City Guides",
      recipientRole: "Editor / Content Creator",
      subjectLine: `Guest Resource / Local Feature: "Smart Living & Renting Appliances in ${primaryCity} - 2026 Guide"`,
      pitchBody: `Hello [Editor Name],\n\nI loved your recent article covering urban living and student life in Madhya Pradesh.\n\nWe have put together an in-depth, data-backed guide on "Rent vs. Buy: How Relocating Professionals Save ₹45,000+ Yearly on Home Appliances in ${primaryCity} & ${secondaryCity}".\n\nWe'd be delighted to provide an exclusive, original guest article tailored for your readers, complete with infographics and local cost breakdowns. We only request a contextual reference to our resource at RentLow.co.in.\n\nLet me know if this sounds of interest!\n\nBest,\nEditorial Team, RentLow`,
      valueProposition: "High quality 1,200-word original localized editorial piece with free infographic assets.",
      followUpTemplate: `Hi [Editor Name], just following up to see if you'd like a draft outline of our "Smart Living in Bhopal" guide for your publication.`
    },
    {
      id: "outreach_movers",
      targetNiche: "Packers & Movers Companies in Bhopal & Sehore",
      recipientRole: "Business Operations Manager",
      subjectLine: `Cross-Promotion: Free Appliance Delivery Perk for Your Relocation Customers`,
      pitchBody: `Dear Partner,\n\nWhen families and executives relocate to ${primaryCity}, transport heavy appliances can be costly and prone to transit damage. RentLow provides a turnkey alternative with pre-installed, high-efficiency appliances on rent.\n\nWe'd love to cross-promote your packing & moving services to our 500+ monthly rental subscribers in exchange for placing RentLow appliance rental brochures or web links in your move-in packages.\n\nLet's connect this week!\n\nRegards,\nRentLow Operations`,
      valueProposition: "Direct customer referrals to active moving clients + co-branded promotional assets.",
      followUpTemplate: `Hi [Name], following up on our movers & packers cross-promotion package in MP Nagar.`
    }
  ];

  // 3. Social Signal Campaigns (Multi-Platform Syndication)
  const socialSignals: SocialSignalCampaign[] = [
    {
      id: "soc_instagram",
      platform: "Instagram",
      postCopy: `✨ Relocating to Bhopal or Sehore? Don't spend ₹60,000 buying new appliances!\n\n❄️ 1.5 Ton Split AC @ ₹499/mo\n🧊 Inverter Refrigerators @ ₹399/mo\n🧺 Automatic Washing Machines @ ₹449/mo\n\n✅ ₹0 Online Advance Payment\n✅ 100% Refundable Security Deposit in Escrow\n✅ Free 24-Hour Doorstep Installation\n✅ Free Lifetime Maintenance & Repairs\n\n👉 Book your appliance in 60 seconds on RentLow.co.in (Link in Bio!)`,
      hashtags: ["#Bhopal", "#Sehore", "#BhopalRentals", "#MPNagar", "#AreraColony", "#BhopalStudents", "#ApplianceOnRent", "#RentLowIndia", "#SmartLivingMP"],
      callToAction: "Visit RentLow.co.in or WhatsApp +91 93006 91533",
      targetAudience: "Students, young professionals, and relocating families in Bhopal & Sehore"
    },
    {
      id: "soc_facebook",
      platform: "Facebook",
      postCopy: `📢 Attention Bhopal & Sehore Residents! Looking to beat the summer heat or furnish your flat without high upfront costs?\n\nRentLow.co.in brings you premium, energy-efficient Home Appliances on monthly rent:\n🔹 Air Conditioners (Split & Window)\n🔹 Refrigerators (Single & Double Door)\n🔹 Washing Machines (Top & Front Load)\n🔹 Coolers & Water Purifiers\n\n🎯 ⚡ ₹0 Advance Online • Pay on Delivery | Free Doorstep Delivery\n\n📍 Serving MP Nagar, Arera Colony, Kolar, Ayodhya Bypass, Indrapuri, Sehore & all nearby areas.\n\nVisit: https://rentlow.co.in | Call/WhatsApp: +91 93006 91533`,
      hashtags: ["#BhopalRentals", "#BhopalFlats", "#BhopalRealEstate", "#SehoreNews", "#RentLow"],
      callToAction: "Order online at RentLow.co.in",
      targetAudience: "Local Bhopal community & buy/sell/rent groups"
    },
    {
      id: "soc_reddit",
      platform: "Reddit",
      postCopy: `[Local Guide] Where to rent ACs, fridges, and appliances in Bhopal without security deposit scams?\n\nHey r/Bhopal! If anyone is moving to MP Nagar, MANIT area, or Kolar for college/jobs, check out RentLow (rentlow.co.in). Unlike local unorganized shops that hold deposits hostage, RentLow offers 100% escrow-backed deposits, zero advance online payments (pay after delivery), and free repairs. Monthly rents start at ₹299. Hope this helps anyone setting up their place!`,
      hashtags: ["#Bhopal", "#MadhyaPradesh", "#StudentLife"],
      callToAction: "Check out rentlow.co.in",
      targetAudience: "r/Bhopal, r/MadhyaPradesh community members"
    },
    {
      id: "soc_twitter",
      platform: "Twitter / X",
      postCopy: `Setting up a new home in #Bhopal or #Sehore? 🏠\n\nRent 5-Star Split ACs, Fridges & Washing Machines starting at ₹299/mo from @RentLowIndia.\n\n⚡ Zero Deposit\n🚚 24-hr Doorstep Delivery\n🔧 Free Lifetime Maintenance\n\nBook now: https://rentlow.co.in 🚀`,
      hashtags: ["#Bhopal", "#Sehore", "#ApplianceRental", "#ZeroDeposit", "#RentLow"],
      callToAction: "https://rentlow.co.in",
      targetAudience: "MP tech workers and relocating professionals"
    }
  ];

  // 4. 5-Star Review Acquisition Blueprints (WhatsApp / SMS)
  const reviewAcquisition = {
    whatsappTemplate: `Hi [Customer Name]! 👋 Thank you for choosing RentLow for your [Appliance Name] rental in ${primaryCity}. We hope the installation went smoothly!\n\nCould you take 10 seconds to share your 5-star experience on Google? It helps our local team immensely:\n⭐ https://share.google/M0s46FKcwmrKlAJ4V\n\nAs a token of appreciation, you'll receive ₹100 off your next monthly invoice! 🙌`,
    smsTemplate: `Hi [Name], thank you for renting with RentLow ${primaryCity}! Please share your 5-star rating on Google: https://share.google/M0s46FKcwmrKlAJ4V to get ₹100 off next month!`,
    googleReviewDeepLink: DEFAULT_RENTLOW_NAP.googleMapsUrl,
    technicianQrScript: `Show the delivery QR flyer to customer after demonstrating cooling/wash cycle: "Sir/Madam, please scan to leave a quick 5-star review on Google for a ₹100 instant cash credit!"`
  };

  // 5. Local Press Release Blueprint
  const pressRelease = {
    headline: `RentLow Expands Zero-Deposit Appliance & Furniture Rental Fleet across ${primaryCity} and ${secondaryCity}`,
    subheadline: `Revolutionary Rental Platform Introduces 24-Hour Guaranteed Doorstep Delivery and Free Lifetime Maintenance for Students and Relocating Professionals.`,
    dateline: `${primaryCity.toUpperCase()}, MADHYA PRADESH - ${new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}`,
    summary: `RentLow India (RentLow.co.in) has officially announced the expansion of its next-generation home appliance rental operations across all key localities of Bhopal and Sehore, offering 5-star inverter air conditioners, refrigerators, washing machines, and coolers with zero online advance payment and 100% refundable escrow security deposits.`,
    body: `With thousands of students and corporate professionals relocating to Madhya Pradesh's capital every year, high upfront purchasing costs and tedious maintenance have remained significant pain points. RentLow addresses this by offering flexible month-to-month appliance subscriptions starting at just ₹299/month.\n\n"Our mission is to eliminate the friction of furnishing a home," said the Operations Lead at RentLow. "Every appliance in our fleet is pre-tested for energy efficiency, delivered free within 24 hours, and supported by complimentary lifetime repair maintenance."\n\nResidents across MP Nagar, Arera Colony, Kolar Road, Ayodhya Bypass, Indrapuri, and Sehore can now book directly on RentLow.co.in without paying anything upfront.`,
    mediaContact: `Press & Media Inquiries: ${DEFAULT_RENTLOW_NAP.supportEmail} | Official Website: ${siteUrl}`
  };

  return {
    napConsistency: DEFAULT_RENTLOW_NAP,
    citations,
    backlinkOutreach,
    socialSignals,
    reviewAcquisition,
    pressRelease,
    offPageReadinessScore: 98,
    appliedTimestamp: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}

/**
 * Save On-Page SEO Config to Firestore
 */
export async function saveOnPageConfigToDatabase(config: OnPageSeoConfig): Promise<void> {
  try {
    await setDoc(doc(db, "settings", "onpage_seo_config"), config, { merge: true });
    localStorage.setItem("rentlow_live_onpage_seo_config", JSON.stringify(config));
  } catch (err) {
    console.error("Firestore onpage_seo_config save error:", err);
    localStorage.setItem("rentlow_live_onpage_seo_config", JSON.stringify(config));
  }
}

/**
 * Save Off-Page SEO Config to Firestore
 */
export async function saveOffPageConfigToDatabase(config: OffPageSeoConfig): Promise<void> {
  try {
    await setDoc(doc(db, "settings", "offpage_seo_config"), config, { merge: true });
    localStorage.setItem("rentlow_live_offpage_seo_config", JSON.stringify(config));
  } catch (err) {
    console.error("Firestore offpage_seo_config save error:", err);
    localStorage.setItem("rentlow_live_offpage_seo_config", JSON.stringify(config));
  }
}
